import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useClientData } from '../hooks/useClientData';
import axios from 'axios';
import { usePermissions } from '../context/PermissionsContext';

const API_URL = process.env.REACT_APP_API_URL;

interface Underwriter {
  id: number;
  status: 'Active' | 'Inactive';
  underwriter_name: string;
  consultant_reference_number: string;
  tax_number: string;
  consultant_name: string;
  consultant_surname: string;
  consultant_phone_number: string;
  consultant_email: string;
  quotations_email: string;
  claims_email: string;
  instructions_email: string;
}

// Confirmation Modal Component
const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  underwriter 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onConfirm: () => void; 
  underwriter: Underwriter | null;
}) => {
  if (!isOpen || !underwriter) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Confirm Deletion</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete underwriter <strong>{underwriter.underwriter_name}</strong>? 
          This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Underwriter Modal Component
const AddUnderwriterModal = ({ 
  isOpen, 
  onClose, 
  onSave 
}: { 
  isOpen: boolean; 
  onClose: () => void; 
  onSave: (underwriterData: Partial<Underwriter>) => Promise<void>;
}) => {
  const [formData, setFormData] = useState<Partial<Underwriter>>({
    underwriter_name: '',
    consultant_reference_number: '',
    tax_number: '',
    consultant_name: '',
    consultant_surname: '',
    consultant_phone_number: '',
    consultant_email: '',
    quotations_email: '',
    claims_email: '',
    instructions_email: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await onSave(formData);
      setFormData({
        underwriter_name: '',
        consultant_reference_number: '',
        tax_number: '',
        consultant_name: '',
        consultant_surname: '',
        consultant_phone_number: '',
        consultant_email: '',
        quotations_email: '',
        claims_email: '',
        instructions_email: ''
      });
    } catch (error) {
      console.error('Error saving underwriter:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-screen overflow-y-auto">
        <h2 className="text-xl font-bold mb-6 text-gray-800">Add New Underwriter</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">Underwriter Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Underwriter Name</label>
                <input
                  type="text"
                  name="underwriter_name"
                  value={formData.underwriter_name || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Reference</label>
                <input
                  type="text"
                  name="consultant_reference_number"
                  value={formData.consultant_reference_number || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tax Number</label>
                <input
                  type="text"
                  name="tax_number"
                  value={formData.tax_number || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-700 border-b pb-2">Consultant Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Name</label>
                <input
                  type="text"
                  name="consultant_name"
                  value={formData.consultant_name || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Surname</label>
                <input
                  type="text"
                  name="consultant_surname"
                  value={formData.consultant_surname || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Phone</label>
                <input
                  type="tel"
                  name="consultant_phone_number"
                  value={formData.consultant_phone_number || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Email</label>
                <input
                  type="email"
                  name="consultant_email"
                  value={formData.consultant_email || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quotations Email</label>
                <input
                  type="email"
                  name="quotations_email"
                  value={formData.quotations_email || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Claims Email</label>
                <input
                  type="email"
                  name="claims_email"
                  value={formData.claims_email || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Instructions Email</label>
                <input
                  type="email"
                  name="instructions_email"
                  value={formData.instructions_email || ''}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {isLoading ? 'Saving...' : 'Save Underwriter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Underwriters = () => {
  const { can } = usePermissions();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [underwriters, setUnderwriters] = useState<Underwriter[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [underwriterToDelete, setUnderwriterToDelete] = useState<Underwriter | null>(null);
  const { id: clientId } = useParams();
  const { user, client, isLoading } = useClientData(clientId);
  const navigate = useNavigate();

  const fetchUnderwriters = async () => {
    setIsLoadingData(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/underwriters`);
      
      if (response.status >= 200 && response.status < 300) {
        const result = response.data;
        
        if (result.success && Array.isArray(result.data)) {
          setUnderwriters(result.data);
        } else if (Array.isArray(result)) {
          setUnderwriters(result);
        } else {
          throw new Error('Unexpected API response format');
        }
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = (err instanceof Error) ? err.message : 'An unknown error occurred.';
      setError(errorMessage);
      console.error('Error in fetchUnderwriters:', err);
    } finally {
      setIsLoadingData(false);
    }
  };

  useEffect(() => {
    fetchUnderwriters();
  }, []);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const handleAddUnderwriter = () => {
    setIsModalOpen(true);
  };

  const handleSaveUnderwriter = async (underwriterData: Partial<Underwriter>) => {
    try {
        console.log('📤 Sending data to backend:', underwriterData);
      const response = await axios.post(`${API_URL}/api/underwriters`, underwriterData);
      
      if (response.status >= 200 && response.status < 300) {
        await fetchUnderwriters();
        setIsModalOpen(false);
        alert('Underwriter added successfully!');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err: any) {
        console.error('❌ Full error object:', err);
        console.error('❌ Error response data:', err.response?.data);
        console.error('❌ Error response status:', err.response?.status);

        const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'An unknown error occurred.';
        console.error('Error saving underwriter:', err);
        alert('Failed to add underwriter: ' + errorMessage);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDeleteClick = (underwriter: Underwriter) => {
    setUnderwriterToDelete(underwriter);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setUnderwriterToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!underwriterToDelete) return;
    
    setDeleteLoading(true);
    try {
      const response = await axios.delete(`${API_URL}/api/underwriters/${underwriterToDelete.id}`);
      
      if (response.status >= 200 && response.status < 300) {
        setUnderwriters(prev => prev.filter(uw => uw.id !== underwriterToDelete.id));
        alert('Underwriter deleted successfully!');
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = (err instanceof Error) ? err.message : 'An unknown error occurred.';
      console.error('Error deleting underwriter:', err);
      alert('Failed to delete underwriter: ' + errorMessage);
    } finally {
      setDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setUnderwriterToDelete(null);
    }
  };

  if (isLoading || isLoadingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  if (!user) return <div>Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />
      
      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title=""
          user={user}
          client={null}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />
        
        {/* Main content area */}
        <div className="flex-1 p-6">
          {/* Header with title and Add button */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Underwriters Management</h2>
            {can('insurers', 'write') && (
              <button
                onClick={handleAddUnderwriter}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors font-medium flex items-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add New Underwriter
              </button>
            )}
          </div>
          
          {/* Modern Underwriters Table */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            {/* Table Header */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-800">Underwriters List</h3>
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Broker Code
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consultant Name
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consultant Surname
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone Number
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Consultant Email
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {underwriters.map((underwriter) => (
                    <tr key={underwriter.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => navigate(`/underwriters/${underwriter.id}`)}
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                        >
                          {underwriter.underwriter_name}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600 bg-blue-100 px-2 py-1 rounded-md inline-block">
                          {underwriter.consultant_reference_number}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{underwriter.consultant_name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{underwriter.consultant_surname}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{underwriter.consultant_phone_number}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{underwriter.consultant_email}</div>
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        <p
                          className={`font-medium ${
                            underwriter.status === 'Active' ? 'text-green-700' : 'text-red-700'
                          }`}
                        >
                          {underwriter.status || 'Active'}
                        </p>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Empty state */}
            {underwriters.length === 0 && (
              <div className="text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No underwriters</h3>
                <p className="mt-1 text-sm text-gray-500">Get started by adding a new underwriter.</p>
                {can('insurers', 'write') && (
                  <div className="mt-6">
                    <button
                      onClick={handleAddUnderwriter}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add Underwriter
                    </button>
                  </div>
                )}

              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Underwriter Modal */}
      <AddUnderwriterModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveUnderwriter}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        underwriter={underwriterToDelete}
      />
    </div>
  );
};

export default Underwriters;